import type { EaseStamp } from "daedalus-renderer-phaser";
import type { SkyOverride } from "odyssey-shared";

/**
 * The page's control of the light layer, which the report carries (`report.ts`): the sky override a server or GM hook
 * switches on, the development switches a capture or a browser test sets, and what the last frame drew.
 */
export interface LightingControl {
  /** Whether the darkness is drawn at all; false shows the world as before the light cycle. */
  enabled: boolean;
  /** The sky in force: the everyday curve, or a named override for a rare event (ADR-141's amendment of 2026-10-31). */
  sky: SkyOverride;
  /** A minute of the turn that holds the clock still for a capture; null follows the wall clock. */
  minute: number | null;
  /** A game day that holds the moons still for a capture; null follows the wall clock. */
  day: number | null;
  /** Where lights come from: the booted map's lit props (`map`, the default), none, or 48 lights laid over the view for the allocation case of the browser suite (`bench`, set by a test only). */
  lights: "map" | "none" | "bench";
  /** Whether the developer slider is mounted (`?lights=dev`). */
  slider: boolean;
  /** The weather's dim, 0 for none up to 1, which the weather view writes each frame (ADR-144, P2.27-T2); it never stacks with the sky: the darker of the two holds. */
  weatherDim: number;
  /** The art stamps of the places a roof that shelters the local character covers, on which the light layer spares `ROOF_LIGHT_EASE` of the darkness, the night's and the weather's dim alike (the author's ruling of 2026-11-01); the scene writes them each frame, and the list is replaced when a roof shelters the character or stops. */
  roofStamps: readonly EaseStamp[];
  /** The dim of the map the page stands on when it is indoors, which holds its light at one minus the dim by day and spares `INDOOR_LIGHT_EASE` of the night's darkness; null on a sky map. */
  indoor: { readonly dim: number } | null;
  /** The server's time the last frame was lit by, or null while no `welcome` has told the page the time (the world is then at full light). */
  serverTime: number | null;
  /** The world rectangle the darkness covered in the last frame, in world pixels, and the canvas it was drawn for in device pixels. */
  view: { left: number; top: number; width: number; height: number; deviceWidth: number; deviceHeight: number };
  /** What the last frame drew. */
  ambient: number;
  tint: number;
  fills: number;
  cuts: number;
  glows: number;
  /** The map's lit props, and how many show their unlit art now (ADR-141's amendment of 2026-11-01). */
  mapLights: number;
  unlitShown: number;
}

/**
 * Makes the control at its defaults: on, the everyday sky, a sky map, the server's clock and the map's lights.
 *
 * @returns The control.
 */
export function createLightingControl(): LightingControl {
  return { enabled: true, sky: "none", minute: null, day: null, lights: "map", slider: false, indoor: null, roofStamps: [], weatherDim: 0, serverTime: null, view: { left: 0, top: 0, width: 0, height: 0, deviceWidth: 0, deviceHeight: 0 }, ambient: 1, tint: 0, fills: 0, cuts: 0, glows: 0, mapLights: 0, unlitShown: 0 };
}

/**
 * Reads `?lights=dev` and `?light=off` from the game page's query (`page-query.ts`, a development page's alone) into a control.
 *
 * @param control The control to set.
 * @param query The page's query as `readPageQuery` gives it.
 * @returns `control`.
 */
export function readLightingQuery(control: LightingControl, query: URLSearchParams): LightingControl {
  if (query.get("lights") === "dev") {
    control.slider = true;
  }
  if (query.get("light") === "off") {
    control.enabled = false;
  }
  return control;
}
